import { useEffect, useState, lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate, useParams, Outlet, useLocation } from "react-router-dom";
import "@/App.css";
import { StoreProvider, useStore } from "./context/Store";
import Header from "./components/Header";
import Footer from "./components/Footer";
import CookieBanner from "./components/CookieBanner";
import PromoBar from "./components/PromoBar";
import PaymentRescue from "./components/PaymentRescue";
import CustomerImpersonationBanner from "./components/CustomerImpersonationBanner";
import { readCityChoice } from "./components/CityPickerModal";
import Home from "./pages/Home";
import Resolver from "./pages/Resolver";
import Product from "./pages/Product";
import Checkout from "./pages/Checkout";
import Confirmed from "./pages/Confirmed";

import Track from "./pages/Track";
import StaticPage from "./pages/Static";
import LegalPage from "./pages/Legal";
import Account, { ResetPassword } from "./pages/Account";
import { CitiesList, OccasionsList, GuidesList, SearchPage } from "./pages/Lists";
import FloristApp from "./pages/florist/FloristApp";
import FloristApplyPage from "./pages/florist/FloristApplyPage";
import DriverLanding from "./pages/driver/DriverLanding";
import AdminApp from "./pages/admin/AdminApp";
import AcceptOrder from "./pages/AcceptOrder";
import DeliverLocation from "./pages/DeliverLocation";
import DriverApp from "./pages/driver/DriverApp";
import MarketingLoader from "./components/MarketingLoader";
import { LOCALES, PATTERNS } from "./i18n";

const CityPickerModal = lazy(() => import("./components/CityPickerModal"));
// Legacy segments kept so old links still count as being inside a city.
const CITY_CONTEXT = new RegExp(`/(${[...new Set(["envio-flores", "توصيل-الورود",
  ...Object.values(PATTERNS).flatMap((p) => [p.city, p.product])])].join("|")})/`);

// Preserve the legacy payment-return URL's locale and full query string.
const ConfirmedRescue = () => {
  const { locale } = useParams();
  const { search } = useLocation();
  return <Navigate to={`/${locale}/order/confirmed${search}`} replace />;
};

const CityPickerLauncher = () => {
  const { boot } = useStore();
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  // Show the picker on first visit (or 30d expiry) once we know at least one
  // city has stock. Skip when the visitor already deep-linked to a specific
  // city/product page — they've expressed intent.
  useEffect(() => {
    if (!boot?.cities?.length) return;
    if (readCityChoice()) return;
    const inCityContext = CITY_CONTEXT.test(pathname);
    if (inCityContext) return;
    // Skip on partner recruitment pages — florists don't need to pick a delivery city.
    if (/\/florist\/apply$/.test(pathname)) return;
    if (/\/driver\/apply$/.test(pathname)) return;
    // Transactional / account pages: never interrupt a reset, checkout, tracking or confirmation.
    if (/\/(account|checkout|track|order|deliver|unsubscribe|terms|terms-of-use|privacy|refunds|delivery|cookies|driver-agreement|florist-agreement|florist-privacy)(\/|$)/.test(pathname)) return;
    const timer = setTimeout(() => setOpen(true), 900); // let the hero paint first for LCP
    return () => clearTimeout(timer);
  }, [boot, pathname]);
  // Expose a global function so the header pill can reopen the modal without prop drilling.
  useEffect(() => {
    window.fleurinelleOpenCityPicker = () => setOpen(true);
    return () => { delete window.fleurinelleOpenCityPicker; };
  }, []);
  if (!open) return null;
  return (
    <Suspense fallback={null}>
      <CityPickerModal onClose={() => setOpen(false)} />
    </Suspense>
  );
};

const Shell = () => {
  const { locale = "en" } = useParams();
  const { pathname } = useLocation();
  const safe = LOCALES.includes(locale) ? locale : "en";
  // enclosed checkout: no site chrome between the basket and the thank-you page
  const enclosed = /\/(checkout|order\/confirmed)$/.test(pathname);
  return (
    <StoreProvider locale={safe}>
      <div className="min-h-screen flex flex-col bg-plaster">
        <CustomerImpersonationBanner />
        {enclosed ? null : <PaymentRescue />}
        {enclosed ? null : <PromoBar />}
        {enclosed ? null : <Header />}
        <main className="flex-1">
          <Outlet />
        </main>
        {enclosed ? null : <Footer />}
        <CookieBanner />
        {enclosed ? null : <CityPickerLauncher />}
      </div>
    </StoreProvider>
  );
};

const ScrollToTop = () => {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) {
      document.getElementById(hash.slice(1))?.scrollIntoView({ behavior: "smooth" });
      return;
    }
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [pathname, hash]);
  return null;
};

const STATIC_PAGES = ["about", "how-it-works", "substitution-policy", "delivery-guarantee", "contact", "partner"];
const LEGAL_PAGES = ["terms", "terms-of-use", "privacy", "refunds", "delivery", "cookies", "florist-agreement", "florist-privacy", "driver-agreement"];
const PRODUCT_SEGMENTS = [...new Set(Object.values(PATTERNS).map((p) => p.product))];
const OCCASION_SEGMENTS = [...new Set(Object.values(PATTERNS).map((p) => p.occasion))];

function App() {
  return (
    <BrowserRouter>
      <ScrollToTop />
      <MarketingLoader />
      <Routes>
        <Route path="/" element={<Navigate to="/en" replace />} />
        <Route path="/florist/apply" element={<Navigate to="/en/florist/apply" replace />} />
        <Route path="/driver/apply" element={<Navigate to="/en/driver/apply" replace />} />
        <Route path="/florist/*" element={<FloristApp />} />
        <Route path="/admin/*" element={<AdminApp />} />
        <Route path="/accept/:token" element={<AcceptOrder />} />
        <Route path="/deliver/:token" element={<DeliverLocation />} />
        <Route path="/driver/*" element={<DriverApp />} />
        <Route path="/:locale" element={<Shell />}>
          <Route index element={<Home />} />
          <Route path="checkout" element={<Checkout />} />
          {/* Rescue path: some early links were emitted as /en/checkout/en/order/confirmed
              (the redirect origin used to include the checkout path). Forward them so
              paying customers still land on the confirmation page. */}
          <Route path="checkout/:locale2/order/confirmed" element={<ConfirmedRescue />} />
          <Route path="order/confirmed" element={<Confirmed />} />
          <Route path="track" element={<Track />} />
          <Route path="account" element={<Account />} />
          <Route path="account/reset" element={<ResetPassword />} />
          <Route path="cities" element={<CitiesList />} />
          {OCCASION_SEGMENTS.map((seg) => (
            <Route key={seg} path={seg} element={<OccasionsList />} />
          ))}
          <Route path="guides" element={<GuidesList />} />
          <Route path="search" element={<SearchPage />} />
          <Route path="florist/apply" element={<FloristApplyPage />} />
          <Route path="driver/apply" element={<DriverLanding />} />
          {PRODUCT_SEGMENTS.map((seg) => (
            <Route key={seg} path={`${seg}/:slug`} element={<Product />} />
          ))}
          {STATIC_PAGES.map((p) => (
            <Route key={p} path={p} element={<StaticPage page={p} />} />
          ))}
          {LEGAL_PAGES.map((p) => (
            <Route key={p} path={p} element={<LegalPage slug={p} />} />
          ))}
          <Route path="*" element={<Resolver />} />
        </Route>
      </Routes>
    </BrowserRouter>
  );
}

export default App;
